import type * as React from "react";
import { cn } from "../../lib/cn";

export interface ProgressBarProps extends React.ComponentProps<"div"> {
  /** Current value, 0..max. */
  readonly value: number;
  /** Defaults to a plain percentage (0..100). */
  readonly max?: number;
  readonly trackClassName?: string;
  readonly barClassName?: string;
}

/**
 * A simple, read-only percentage/progress indicator -- a filled pill track.
 * Nothing here is adjustable, so a plain div pair is enough rather than
 * pulling in Radix's Progress primitive.
 */
export function ProgressBar({
  value,
  max = 100,
  className,
  trackClassName,
  barClassName,
  ...props
}: ProgressBarProps) {
  const percent = max <= 0 ? 0 : Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      className={cn(
        "h-2 w-full overflow-hidden rounded-full bg-container-subtle-background",
        trackClassName,
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "h-full rounded-full bg-primary transition-[width] duration-140 ease-emphasized",
          barClassName,
        )}
        style={{ width: `${String(percent)}%` }}
      />
    </div>
  );
}
